import type {
  BackupDriveState,
  BackupKind,
  BackupOrigin,
  BackupRequestKind,
} from '@/shared/api/resources';

/** Nome do tipo de backup como a tela mostra. */
export const tiposDeBackup: Record<BackupKind, string> = {
  banco: 'Banco de dados',
  arquivos: 'Arquivos enviados',
};

/** O que cada pedido de backup faz, em linguagem de quem pede. */
export const pedidosDeBackup: Record<BackupRequestKind, string> = {
  banco: 'Banco de dados',
  arquivos: 'Arquivos enviados pelas empresas',
  completo: 'Completo (banco e arquivos)',
};

export const origensDeBackup: Record<BackupOrigin, string> = {
  agendado: 'Agendado',
  manual: 'Manual',
};

export const situacoesNoDrive: Record<
  BackupDriveState,
  { rotulo: string; tom: 'neutral' | 'info' | 'success' | 'danger' }
> = {
  nao_enviado: { rotulo: 'Nao enviado', tom: 'neutral' },
  enviando: { rotulo: 'Enviando', tom: 'info' },
  enviado: { rotulo: 'Enviado', tom: 'success' },
  falhou: { rotulo: 'Falhou', tom: 'danger' },
};

/**
 * Resultado do retorno do Google.
 *
 * O servidor devolve um codigo, e nunca um texto: o endereco de chegada pode ser
 * montado por qualquer pessoa, e um texto vindo dele e repetido na tela serviria
 * para mostrar mensagem falsa com a cara da aplicacao.
 */
export interface ResultadoDoGoogle {
  tom: 'success' | 'warning' | 'danger';
  titulo: string;
  texto: string;
}

const resultados: Record<string, ResultadoDoGoogle> = {
  conectado: {
    tom: 'success',
    titulo: 'Conta Google conectada',
    texto: 'Os backups ja podem ser enviados para a pasta no Google Drive.',
  },
  recusado: {
    tom: 'warning',
    titulo: 'Conexao cancelada',
    texto: 'A autorizacao foi recusada na tela do Google. Nada foi conectado.',
  },
  vencido: {
    tom: 'warning',
    titulo: 'O pedido venceu',
    texto:
      'Passaram mais de dez minutos entre o pedido e a volta do Google. Tente conectar de novo.',
  },
  invalido: {
    tom: 'danger',
    titulo: 'Retorno nao reconhecido',
    texto:
      'A volta do Google nao corresponde a um pedido feito por esta tela, ou ja foi usada. Tente conectar de novo.',
  },
  sem_permissao: {
    tom: 'danger',
    titulo: 'Sem permissao',
    texto: 'Quem pediu a conexao nao administra mais a plataforma.',
  },
  sem_escopo: {
    tom: 'warning',
    titulo: 'Falta a permissao do Google Drive',
    texto:
      'Na tela do Google, deixe marcada a opcao de acesso ao Google Drive e conecte de novo.',
  },
  falhou: {
    tom: 'danger',
    titulo: 'Nao foi possivel conectar',
    texto:
      'O Google nao concluiu a conexao. Tente de novo; se repetir, o motivo esta na trilha da plataforma.',
  },
  indisponivel: {
    tom: 'danger',
    titulo: 'Conexao indisponivel',
    texto: 'Este servidor nao esta configurado para o Google Drive.',
  },
};

/** Traduz o codigo do retorno. Codigo desconhecido nao mostra nada. */
export function resultadoDoGoogle(codigo: string | null): ResultadoDoGoogle | null {
  if (!codigo) return null;
  return resultados[codigo] ?? null;
}

/** Tipo da mensagem que a janela do Google manda para a tela de backups. */
export const mensagemDoGoogle = 'seguracore.backups.google';

/**
 * O que cada variavel que falta significa, para quem cuida do servidor.
 *
 * A tela mostra o nome exato, porque e ele que se copia para o servidor, e uma
 * frase do que ele e, porque quem le nem sempre sabe.
 */
export const variaveisDoGoogle: Record<string, string> = {
  SEGURACORE_GOOGLE_CLIENT_ID: 'o ID do cliente OAuth criado no Google Cloud Console',
  SEGURACORE_GOOGLE_CLIENT_SECRET: 'o segredo desse mesmo cliente OAuth',
  SEGURACORE_SECRETS_KEY:
    'a chave que guarda o acesso da conta Google cifrado (gerada com openssl rand -base64 32)',
  SEGURACORE_APP_BASE_URL:
    'o endereco publico da aplicacao, como https://seguracore.com.br',
};

export function explicarVariavel(nome: string): string {
  const chave = nome.split(' ')[0] ?? nome;
  return variaveisDoGoogle[chave] ?? 'configuracao do servidor';
}
